import { STORAGE_BUNTU } from '../constants/storageKeys';
import type { BuntuPrefs } from './types';

/**
 * Preference persistence.
 *
 * Kept out of the engine so the core stays storage-agnostic: a desktop shell
 * would swap this for a config file without touching the state machine.
 */

export const DEFAULT_PREFS: BuntuPrefs = {
  minimized: false,
  hiddenUntil: null,
  snoozedUntil: null,
  intervalMinutes: 60,
  corner: 'bottom-right',
};

export function loadPrefs(): BuntuPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_BUNTU);
    if (!raw) return { ...DEFAULT_PREFS };
    const saved = JSON.parse(raw) as Partial<BuntuPrefs>;
    const prefs = { ...DEFAULT_PREFS, ...saved };
    // Expired suppressions are dropped on load so a stale value cannot keep
    // Buntu tucked away forever.
    const now = Date.now();
    if (prefs.hiddenUntil && prefs.hiddenUntil < now) {
      prefs.hiddenUntil = null;
      // An expired "hide for today" should bring him back automatically.
      if (!prefs.snoozedUntil || prefs.snoozedUntil < now) prefs.minimized = false;
    }
    if (prefs.snoozedUntil && prefs.snoozedUntil < now) {
      prefs.snoozedUntil = null;
      if (!prefs.hiddenUntil) prefs.minimized = false;
    }
    // Older builds hid him with no restore chip — promote that to minimized.
    if (!saved.minimized && prefs.hiddenUntil && prefs.hiddenUntil >= now) {
      prefs.minimized = true;
    }
    prefs.intervalMinutes = Math.min(240, Math.max(5, prefs.intervalMinutes || 60));
    return prefs;
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function savePrefs(prefs: BuntuPrefs): void {
  try {
    localStorage.setItem(STORAGE_BUNTU, JSON.stringify(prefs));
  } catch {
    /* storage disabled or full; Buntu still works for this session */
  }
}
